// AdvancedOrderPopup.js
import React, {useContext, useEffect, useMemo, useState} from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    RadioGroup,
    FormControlLabel,
    Radio,
    MenuItem,
} from "@mui/material";
import "../css/BuySellPopup.css";
import { useStackedPopup } from "@dashboard/contexts/PopupStackContext";

// Builders (adjust these paths to your actual locations)
import { buildConsistentConfig } from "./builders/buildConsistentConfig";
import { buildRangeConfig } from "./builders/buildRangeConfig";
import { buildKeepRiseConfig } from "./builders/buildKeepRiseConfig";
import { buildPreopenConfig } from "./builders/buildPreopenConfig";
import {DataContext} from "@dashboard/contexts/DataContext"; // you said you already created this

const getNextWorkingDay = (fromDate = new Date()) => {
    const d = new Date(fromDate);
    d.setDate(d.getDate() + 1);
    let day = d.getDay(); // 0 = Sun, 6 = Sat
    if (day === 6) d.setDate(d.getDate() + 2);
    else if (day === 0) d.setDate(d.getDate() + 1);
    return d;
};

const formatDisplayDate = (date) => {
    if (!date) return "";
    const d = date instanceof Date ? date : new Date(date);
    if (Number.isNaN(d.getTime())) return "";
    return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
};

const formatDateInput = (d) => d.toISOString().slice(0, 10);

const AdvancedOrderPopup = ({
                                open,
                                order,
                                tradingsymbol,
                                side,
                                basePrice,
                                onClose,
                                onConfirm,
                                backendError,
                                submitting,
                                activeZerodhaUsers = [],
                                lastClosePrice,
                                lastClosePriceDate,
                            }) => {
    const { allInstrumentsList = [] } = useContext(DataContext);

    const isBuy = side === "BUY";

    const instrumentToken =
        order?.instrumentToken ?? order?.instrument_token ?? null;

    const resolvedLotSize = useMemo(() => {
        if (!allInstrumentsList || allInstrumentsList.length === 0) return "";

        // Prefer token match
        if (instrumentToken != null) {
            const found = allInstrumentsList.find(
                (x) => String(x.instrumentToken ?? x.instrument_token) === String(instrumentToken)
            );
            if (found?.lotSize != null) return String(found.lotSize);
        }

        // Fallback: symbol match (if your list has tradingsymbol)
        const sym = (tradingsymbol || order?.tradingsymbol || "").trim();
        if (sym) {
            const found = allInstrumentsList.find(
                (x) => String(x.tradingsymbol ?? x.tradingSymbol ?? "").trim().toUpperCase() === sym.toUpperCase()
            );
            if (found?.lotSize != null) return String(found.lotSize);
        }

        return "";
    }, [allInstrumentsList, instrumentToken, tradingsymbol, order?.tradingsymbol]);

    const displaySymbol =
        (tradingsymbol || order?.tradingsymbol || "").trim();

// AdvancedOrderPopup.js

    const renderDialogTitle = (title) => {
        const displaySymbol =
            (tradingsymbol || order?.tradingsymbol || "").trim();

        return (
            <div
                style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    width: "100%",
                }}
            >
                {/* Left: Title */}
                <div style={{ fontWeight: 600 }}>
                    {title}
                </div>

                {/* Right: Trading Symbol */}
                {displaySymbol && (
                    <div
                        style={{
                            fontSize: 13,
                            fontWeight: 600,
                            opacity: 0.85,
                            letterSpacing: 0.3,
                        }}
                    >
                        {displaySymbol}
                    </div>
                )}
            </div>
        );
    };

    const [step, setStep] = useState(1);
    const [selectedMode, setSelectedMode] = useState("");

    // Core numeric fields
    const [price, setPrice] = useState("");
    const [minPrice, setMinPrice] = useState("");
    const [maxPrice, setMaxPrice] = useState("");
    const [delayMinutes, setDelayMinutes] = useState("");
    const [quantity, setQuantity] = useState("");
    const [amount, setAmount] = useState("");

    // Safeguards – REQUIRED (non-preopen)
    const [singleLargestOrderValue, setSingleLargestOrderValue] = useState("");
    const [totalDurationValue, setTotalDurationValue] = useState("");
    const [totalDurationUnit, setTotalDurationUnit] = useState("HOURS");

    const [error, setError] = useState("");

    // Keep-buying-on-rise specific
    const [keepSymbols, setKeepSymbols] = useState("");
    const [keepPercentRise, setKeepPercentRise] = useState("");
    const [incQtyAbs, setIncQtyAbs] = useState("");
    const [incAmountAbs, setIncAmountAbs] = useState("");
    const [incQtyPct, setIncQtyPct] = useState("");
    const [incAmountPct, setIncAmountPct] = useState("");

    // Execution mode (not a safeguard)
    const [executionMode, setExecutionMode] = useState("AGGRESSIVE");

    // Zerodha user selection (optional in UI, passed for CONSISTENT/RANGE/PREOPEN)
    const [selectedUserId, setSelectedUserId] = useState("");

    // Preopen state
    const [preopenOrderDate, setPreopenOrderDate] = useState("");
    const [preopenPriceMode, setPreopenPriceMode] = useState("CIRCUIT");
    const [customPreopenPrice, setCustomPreopenPrice] = useState("");
    const [preopenTotalOrders, setPreopenTotalOrders] = useState(1);
    const [preopenRepeatTillDays, setPreopenRepeatTillDays] = useState("");

    useStackedPopup(true, () => {
        if (order) {
            onClose && onClose();
            return;
        }
        if (step === 1) onClose && onClose();
        else if (step === 2) handleBackToModes();
    });

    useEffect(() => {
        if (!order) return;

        setSelectedMode(order.mode);
        setPrice(order.targetPrice ?? order.price ?? "");
        setMinPrice(order.minPrice ?? "");
        setMaxPrice(order.maxPrice ?? "");
        setDelayMinutes(order.delayMinutes ?? "");
        setQuantity(order.expectedQuantity ?? "");
        setAmount(order.amount ?? "");

        setSingleLargestOrderValue(order.singleLargestOrderValue ?? "");
        setTotalDurationValue(order.totalDurationHours ?? "");
        setExecutionMode(order.executionMode ?? "AGGRESSIVE");
        setSelectedUserId(order.zerodhaUserId ?? "");

        const preopen = order.preopenConfig || order.preopen || null;

        if (String(order.mode).toUpperCase() === "PREOPEN") {
            const raw = preopen?.preopenOrderDate ?? order?.preopenOrderDate;
            setPreopenOrderDate(typeof raw === "string" ? raw.trim() : "");

            setPreopenPriceMode(
                preopen?.preopenPriceMode ??
                order.preopenPriceMode ??
                "CIRCUIT"
            );

            setCustomPreopenPrice(
                preopen?.preopenCustomPrice ??
                order.preopenCustomPrice ??
                ""
            );

            setPreopenTotalOrders(
                preopen?.preopenTotalOrders ??
                order.preopenTotalOrders ??
                1
            );

            setPreopenRepeatTillDays(
                preopen?.preopenRepeatTillDays ??
                order.preopenRepeatTillDays ??
                ""
            );
        }

    }, [order]);

    useEffect(() => {
        if (!open) return;

        if (!order) setError("");

        if (order) {
            setSelectedMode(order.mode || "");
            setStep(2);
            return;
        }

        const base = basePrice != null && !Number.isNaN(basePrice) ? String(basePrice) : "";
        setPrice(base);
        setMinPrice("");
        setMaxPrice("");
        setDelayMinutes("");
        setQuantity("");
        setAmount("");

        setSingleLargestOrderValue("");
        setTotalDurationValue("");
        setTotalDurationUnit("HOURS");

        setKeepSymbols("");
        setKeepPercentRise("");
        setIncQtyAbs("");
        setIncAmountAbs("");
        setIncQtyPct("");
        setIncAmountPct("");

        setExecutionMode("AGGRESSIVE");

        if (activeZerodhaUsers && activeZerodhaUsers.length === 1) {
            setSelectedUserId(activeZerodhaUsers[0]);
        }
    }, [open, side, basePrice, activeZerodhaUsers, order]);

    const modeOptions = isBuy
        ? [
            { value: "CONSISTENT", label: "Buy consistently above price" },
            { value: "RANGE", label: "Buy within a range timebound" },
            { value: "KEEP_RISE", label: "Keep buying on rise" },
            { value: "PREOPEN", label: "Buy at preopen" },
        ]
        : [
            { value: "CONSISTENT", label: "Sell consistently above price" },
            { value: "RANGE", label: "Sell within a range timebound" },
            { value: "PREOPEN", label: "Sell at preopen" },
        ];

    const modeLocked = Boolean(order);

    const detailsTitle = (() => {
        if (selectedMode === "PREOPEN") return isBuy ? "Buy at preopen" : "Sell at preopen";
        if (selectedMode === "CONSISTENT") return isBuy ? "Buy Consistently" : "Sell Consistently";
        if (selectedMode === "RANGE") return isBuy ? "Buy within a range timebound" : "Sell within a range timebound";
        if (selectedMode === "KEEP_RISE") return "Keep buying on rise";
        return "";
    })();

    const handleCloseAll = () => onClose && onClose();

    const handleMuiCloseStep1 = (event, reason) => {
        if (reason === "backdropClick" || reason === "escapeKeyDown") return;
        handleCloseAll();
    };

    const handleMuiCloseStep2 = (event, reason) => {
        if (reason === "backdropClick" || reason === "escapeKeyDown") return;
        handleCloseAll();
    };

    const handleStep1Next = () => {
        if (!selectedMode) {
            setError("Please select one option.");
            return;
        }
        setError("");
        setStep(2);
    };

    const handleBackToModes = () => {
        setStep(1);
        setError("");
    };

    const handleFinalDone = () => {
        setError("");

        let res = null;

        if (selectedMode === "CONSISTENT") {
            res = buildConsistentConfig({
                side,
                price,
                quantity,
                amount,
                lotSize: resolvedLotSize,
                executionMode,
                singleLargestOrderValue,
                totalDurationValue,
                totalDurationUnit,
                selectedUserId,
            });
        } else if (selectedMode === "RANGE") {
            res = buildRangeConfig({
                side,
                minPrice,
                maxPrice,
                delayMinutes,
                quantity,
                amount,
                lotSize: resolvedLotSize,
                executionMode,
                singleLargestOrderValue,
                totalDurationValue,
                totalDurationUnit,
                selectedUserId,
            });
        } else if (selectedMode === "KEEP_RISE") {
            res = buildKeepRiseConfig({
                side,
                keepSymbols,
                keepPercentRise,
                incQtyAbs,
                incAmountAbs,
                incQtyPct,
                incAmountPct,
                executionMode,
                singleLargestOrderValue,
                totalDurationValue,
                totalDurationUnit,
            });
        } else if (selectedMode === "PREOPEN") {
            res = buildPreopenConfig({
                side,
                zerodhaUserId: selectedUserId || "",
                quantity,
                amount,
                lastClosePrice,
                basePrice,

                preopenOrderDate,
                preopenPriceMode,
                customPreopenPrice,
                preopenTotalOrders,
                preopenRepeatTillDays,
                lotSize: resolvedLotSize,
            });
        } else {
            setError("Please select one option.");
            return;
        }

        if (!res) {
            setError("Unexpected error building order configuration.");
            return;
        }
        if (res.error) {
            setError(res.error);
            return;
        }

        if (res.config) {
            onConfirm && onConfirm(res.config);
        }

        if (res.shouldAutoClose) {
            handleCloseAll();
        }
    };

    const amountLabel = isBuy ? "Amount to buy (₹)" : "Amount to sell (₹)";
    const effectiveError = error || backendError || "";

    const renderUserSelectionRow = () => {
        if (selectedMode !== "CONSISTENT" && selectedMode !== "RANGE" && selectedMode !== "PREOPEN") return null;

        const options = [...activeZerodhaUsers];
        if (selectedUserId && !options.includes(selectedUserId)) options.unshift(selectedUserId);

        return (
            <div className="bsp-adv-row">
                <div className="bsp-field bsp-user-field-small">
                    <label style={{ fontSize: "12px" }}>Zerodha User (optional)</label>

                    <TextField
                        select
                        size="small"
                        value={selectedUserId}
                        onChange={(e) => setSelectedUserId(e.target.value)}
                        sx={{ "& .MuiInputBase-input": { fontSize: "12px" } }}
                    >
                        {options.map((u) => (
                            <MenuItem key={u} value={u} sx={{ fontSize: "12px" }}>
                                {u}
                            </MenuItem>
                        ))}
                        <MenuItem value="" sx={{ fontSize: "12px", opacity: 0.7 }}>
                            (none)
                        </MenuItem>
                    </TextField>
                </div>
            </div>
        );
    };

    const renderExecutionModeRow = () => (
        <div className="bsp-adv-row">
            <div className="bsp-field">
                <label>Execution mode</label>
                <RadioGroup row value={executionMode} onChange={(e) => setExecutionMode(e.target.value)}>
                    <FormControlLabel value="AGGRESSIVE" control={<Radio size="small" />} label="Aggressive" />
                    <FormControlLabel value="RELAXED" control={<Radio size="small" />} label="Relaxed" />
                </RadioGroup>
            </div>
        </div>
    );

    const renderSafeguardsRow = () => (
        <div className="bsp-safeguard-block">
            <div className="bsp-adv-row bsp-adv-row-label">
                <label>Safeguards</label>
            </div>
            <div className="bsp-adv-row">
                <div className="bsp-field">
                    <label>Single largest order value (₹)</label>
                    <div className="bsp-largest-order">
                        <TextField
                            size="small"
                            type="number"
                            value={singleLargestOrderValue}
                            inputProps={{ step: "0.01" }}
                            onChange={(e) => setSingleLargestOrderValue(e.target.value)}
                        />
                    </div>
                </div>
            </div>
            <div className="bsp-adv-row">
                <div className="bsp-field">
                    <label>Total execution duration (optional)</label>
                    <div className="bsp-duration-row">
                        <TextField
                            size="small"
                            type="number"
                            value={totalDurationValue}
                            inputProps={{ step: "0.1", min: 0 }}
                            onChange={(e) => setTotalDurationValue(e.target.value)}
                            placeholder="e.g. 6"
                            className="bsp-duration-value"
                        />
                        <TextField
                            select
                            size="small"
                            value={totalDurationUnit}
                            onChange={(e) => setTotalDurationUnit(e.target.value)}
                            className="bsp-duration-unit"
                            sx={{ "& .MuiInputBase-input": { fontSize: "12px" } }}
                        >
                            <MenuItem sx={{ fontSize: "12px" }} value="MINUTES">
                                Minutes
                            </MenuItem>
                            <MenuItem sx={{ fontSize: "12px" }} value="HOURS">
                                Hours
                            </MenuItem>
                            <MenuItem sx={{ fontSize: "12px" }} value="DAYS">
                                Days
                            </MenuItem>
                        </TextField>
                    </div>
                </div>
            </div>
        </div>
    );

    return (
        <>
            {/* STEP 1 */}
            <Dialog
                open={open && step === 1 && !order}
                onClose={handleMuiCloseStep1}
                maxWidth="xs"
                fullWidth
                PaperProps={{ className: "bsp-advanced-dialog" }}
                disableEscapeKeyDown
                disableEnforceFocus
                disableRestoreFocus
                onMouseDown={(e) => e.stopPropagation()}
                onClick={(e) => e.stopPropagation()}
            >
                <DialogTitle className="bsp-advanced-header">
                    {renderDialogTitle("Advanced Options")}
                </DialogTitle>
                <DialogContent className="bsp-advanced-content">
                    <RadioGroup value={selectedMode} onChange={(e) => !modeLocked && setSelectedMode(e.target.value)} className="bsp-advanced-radio-group">
                        {modeOptions.map((opt) => (
                            <FormControlLabel key={opt.value} value={opt.value} control={<Radio size="small" disabled={modeLocked} />} label={opt.label} />
                        ))}
                    </RadioGroup>

                    {effectiveError && <div className="bsp-message bsp-error">{effectiveError}</div>}
                </DialogContent>
                <DialogActions className="bsp-advanced-actions">
                    <Button onClick={handleCloseAll} disabled={submitting}>
                        Cancel
                    </Button>
                    <Button variant="contained" onClick={handleStep1Next} disabled={submitting}>
                        Done
                    </Button>
                </DialogActions>
            </Dialog>

            {/* STEP 2 */}
            <Dialog
                open={open && step === 2}
                onClose={handleMuiCloseStep2}
                maxWidth="xs"
                fullWidth
                PaperProps={{ className: "bsp-advanced-dialog" }}
                disableEscapeKeyDown
                disableEnforceFocus
                disableRestoreFocus
                onMouseDown={(e) => e.stopPropagation()}
                onClick={(e) => e.stopPropagation()}
            >
                <DialogTitle className="bsp-advanced-header">
                    {renderDialogTitle(detailsTitle)}
                </DialogTitle>
                <DialogContent className="bsp-advanced-content">
                    {renderUserSelectionRow()}
                    {selectedMode !== "PREOPEN" && renderExecutionModeRow()}

                    {/* --- PREOPEN UI (unchanged) --- */}
                    {selectedMode === "PREOPEN" && (
                        <>
                            <div className="bsp-adv-row">
                                <div className="bsp-field">
                                    <label>Last close price</label>
                                    <TextField
                                        size="small"
                                        value={lastClosePrice || ""}
                                        disabled
                                        sx={{
                                            "& .MuiInputBase-input.Mui-disabled": {
                                                WebkitTextFillColor: "#444",   // readable
                                                opacity: 0.9,
                                                cursor: "default"
                                            }
                                        }}
                                    />                                </div>
                                <div className="bsp-field">
                                    <label>Close date</label>
                                    <TextField
                                        size="small"
                                        value={formatDisplayDate(lastClosePriceDate)}
                                        disabled
                                        sx={{
                                            "& .MuiInputBase-input.Mui-disabled": {
                                                WebkitTextFillColor: "#444",   // readable
                                                opacity: 0.9,
                                                cursor: "default"
                                            }
                                        }}
                                    />                                </div>
                            </div>

                            <div className="bsp-adv-row">
                                <div className="bsp-field">
                                    <label>Order to be placed on</label>
                                    <TextField size="small" type="date" value={preopenOrderDate} onChange={(e) => setPreopenOrderDate(e.target.value)} />
                                </div>

                                <div className="bsp-field">
                                    <label>Order price</label>
                                    <RadioGroup row value={preopenPriceMode} onChange={(e) => setPreopenPriceMode(e.target.value)}>
                                        <FormControlLabel value="CIRCUIT" control={<Radio size="small" />} label={isBuy ? "Upper circuit" : "Lower circuit"} />
                                        <FormControlLabel value="CUSTOM" control={<Radio size="small" />} label="Custom price" />
                                    </RadioGroup>

                                    {preopenPriceMode === "CUSTOM" && (
                                        <TextField
                                            size="small"
                                            type="number"
                                            value={customPreopenPrice}
                                            inputProps={{ step: "0.01" }}
                                            onChange={(e) => setCustomPreopenPrice(e.target.value)}
                                            style={{ marginTop: 8 }}
                                        />
                                    )}
                                </div>
                            </div>
                            <div className="bsp-adv-row">
                                <div className="bsp-field">
                                    <div style={{ fontSize: 13, fontWeight: 600 }}>
                                        {resolvedLotSize ? `Lot size: ${resolvedLotSize}` : "Lot size: -"}
                                    </div>
                                </div>
                            </div>

                            <div className="bsp-verticl-comp-div bsp-adv-optional-block">
                                <div className="bsp-adv-row bsp-adv-row-label">
                                    <label>Either quantity or {amountLabel} (optional)</label>
                                </div>

                                <div className="bsp-adv-row">
                                    <div className="bsp-field">
                                        <label>Total quantity</label>
                                        <TextField size="small" type="number" value={quantity} inputProps={{ step: "1" }} onChange={(e) => setQuantity(e.target.value)} />
                                    </div>
                                    <div className="bsp-field">
                                        <label>{amountLabel} per order</label>
                                        <TextField size="small" type="number" value={amount} inputProps={{ step: "0.01" }} onChange={(e) => setAmount(e.target.value)} />
                                    </div>
                                </div>
                            </div>

                            <div className="bsp-adv-row">
                                <div className="bsp-field">
                                    <label>Total number of orders to be placed</label>
                                    <TextField
                                        size="small"
                                        type="number"
                                        value={preopenTotalOrders}
                                        inputProps={{ step: "1", min: 1 }}
                                        onChange={(e) => setPreopenTotalOrders(e.target.value)}
                                    />
                                </div>

                                <div className="bsp-field">
                                    <label>Repeat till days</label>
                                    <TextField
                                        size="small"
                                        type="number"
                                        value={preopenRepeatTillDays}
                                        inputProps={{ step: "1", min: 0 }}
                                        onChange={(e) => setPreopenRepeatTillDays(e.target.value)}
                                    />
                                </div>
                            </div>
                        </>
                    )}

                    {/* --- CONSISTENT UI (unchanged) --- */}
                    {selectedMode === "CONSISTENT" && (
                        <>
                            <div className="bsp-adv-row">
                                <div className="bsp-field">
                                    <label>{isBuy ? "Buy consistently at price" : "Sell consistently at price"}</label>
                                    <TextField size="small" type="number" value={price} inputProps={{ step: "0.01" }} onChange={(e) => setPrice(e.target.value)} />
                                </div>
                            </div>
                            <div className="bsp-adv-row">
                                <div className="bsp-field">
                                    <div style={{ fontSize: 13, fontWeight: 600 }}>
                                        {resolvedLotSize ? `Lot size: ${resolvedLotSize}` : "Lot size: -"}
                                    </div>
                                </div>
                            </div>

                            <div className="bsp-verticl-comp-div bsp-adv-optional-block">
                                <div className="bsp-adv-row bsp-adv-row-label">
                                    <label>Either expected quantity or {amountLabel} (optional)</label>
                                </div>

                                <div className="bsp-adv-row">
                                    <div className="bsp-field">
                                        <label>Expected quantity</label>
                                        <TextField size="small" type="number" value={quantity} inputProps={{ step: "1" }} onChange={(e) => setQuantity(e.target.value)} />
                                    </div>
                                    <div className="bsp-field">
                                        <label>{amountLabel}</label>
                                        <TextField size="small" type="number" value={amount} inputProps={{ step: "0.01" }} onChange={(e) => setAmount(e.target.value)} />
                                    </div>
                                </div>
                            </div>

                            {renderSafeguardsRow()}
                        </>
                    )}

                    {/* --- RANGE UI (unchanged) --- */}
                    {selectedMode === "RANGE" && (
                        <>
                            <div className="bsp-adv-row">
                                <div className="bsp-field">
                                    <label>Lowest price</label>
                                    <TextField size="small" type="number" value={minPrice} inputProps={{ step: "0.01" }} onChange={(e) => setMinPrice(e.target.value)} />
                                </div>
                                <div className="bsp-field">
                                    <label>Highest price</label>
                                    <TextField size="small" type="number" value={maxPrice} inputProps={{ step: "0.01" }} onChange={(e) => setMaxPrice(e.target.value)} />
                                </div>
                            </div>

                            <div className="bsp-adv-row">
                                <div className="bsp-field">
                                    <div style={{ fontSize: 13, fontWeight: 600 }}>
                                        {resolvedLotSize ? `Lot size: ${resolvedLotSize}` : "Lot size: -"}
                                    </div>
                                </div>
                            </div>

                            <div className="bsp-verticl-comp-div bsp-adv-optional-block">
                                <div className="bsp-adv-row bsp-adv-row-label">
                                    <label>Either expected quantity or {amountLabel} (optional)</label>
                                </div>

                                <div className="bsp-adv-row">
                                    <div className="bsp-field">
                                        <label>Expected quantity</label>
                                        <TextField size="small" type="number" value={quantity} inputProps={{ step: "1" }} onChange={(e) => setQuantity(e.target.value)} />
                                    </div>
                                    <div className="bsp-field">
                                        <label>{amountLabel}</label>
                                        <TextField size="small" type="number" value={amount} inputProps={{ step: "0.01" }} onChange={(e) => setAmount(e.target.value)} />
                                    </div>
                                </div>
                            </div>

                            <div className="bsp-adv-row">
                                <div className="bsp-field">
                                    <label>Delay (minutes)</label>
                                    <TextField size="small" type="number" value={delayMinutes} inputProps={{ step: "0.01", min: 0.01 }} onChange={(e) => setDelayMinutes(e.target.value)} />
                                </div>
                            </div>

                            {renderSafeguardsRow()}
                        </>
                    )}

                    {/* --- KEEP_RISE UI (unchanged) --- */}
                    {selectedMode === "KEEP_RISE" && (
                        <>
                            <div className="bsp-adv-row">
                                <div className="bsp-field">
                                    <label>Symbol name(s)</label>
                                    <TextField
                                        size="small"
                                        value={keepSymbols}
                                        onChange={(e) => setKeepSymbols(e.target.value)}
                                        placeholder="e.g. TCS,INFY,HDFCBANK"
                                        helperText="Comma separated list if you want this rule on multiple symbols."
                                    />
                                </div>
                            </div>

                            <div className="bsp-adv-row">
                                <div className="bsp-field">
                                    <label>Buy on % rise from last buy price</label>
                                    <TextField size="small" type="number" value={keepPercentRise} inputProps={{ step: "0.01" }} onChange={(e) => setKeepPercentRise(e.target.value)} />
                                </div>
                            </div>

                            <div className="bsp-verticl-comp-div">
                                <div className="bsp-adv-row bsp-adv-row-label">
                                    <label>Any ONE of these four</label>
                                </div>

                                <div className="bsp-adv-row">
                                    <div className="bsp-field">
                                        <label>Incremental buy quantity (shares)</label>
                                        <TextField size="small" type="number" value={incQtyAbs} inputProps={{ step: "1" }} onChange={(e) => setIncQtyAbs(e.target.value)} />
                                    </div>
                                    <div className="bsp-field">
                                        <label>Incremental buy amount (₹)</label>
                                        <TextField size="small" type="number" value={incAmountAbs} inputProps={{ step: "0.01" }} onChange={(e) => setIncAmountAbs(e.target.value)} />
                                    </div>
                                </div>

                                <div className="bsp-adv-row">
                                    <div className="bsp-field">
                                        <label>Incremental qty (% of holding qty)</label>
                                        <TextField size="small" type="number" value={incQtyPct} inputProps={{ step: "0.01" }} onChange={(e) => setIncQtyPct(e.target.value)} />
                                    </div>
                                    <div className="bsp-field">
                                        <label>Incremental amount (% of holding value)</label>
                                        <TextField size="small" type="number" value={incAmountPct} inputProps={{ step: "0.01" }} onChange={(e) => setIncAmountPct(e.target.value)} />
                                    </div>
                                </div>
                            </div>

                            {renderSafeguardsRow()}
                        </>
                    )}

                    {effectiveError && <div className="bsp-message bsp-error">{effectiveError}</div>}
                </DialogContent>

                <DialogActions className="bsp-advanced-actions">
                    {order ? (
                        <Button onClick={handleCloseAll} disabled={submitting}>
                            Close
                        </Button>
                    ) : (
                        <Button onClick={handleBackToModes} disabled={submitting}>
                            Back
                        </Button>
                    )}

                    <Button variant="contained" onClick={handleFinalDone} disabled={submitting}>
                        Done
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

AdvancedOrderPopup.defaultProps = {
    backendError: "",
    submitting: false,
    activeZerodhaUsers: [],
};

export default AdvancedOrderPopup;
